import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, ArrowLeft } from 'lucide-react';
import { approvalService, GetFinalDecisionPayload, eventCycleService, CycleEvent, submissionService, Category, FinalDecisionSubmission } from '../services/adminAuth';
import ReusableTable, { TableColumn } from '../components/adminTable';
import { useNavigate } from 'react-router-dom';
// import ExportButton from '../../sharedComponents/ExportButton';

function FinalAssessment() {
    const navigate = useNavigate();
    const [applicationType, setApplicationType] = useState<number>(1);
    const [categoryId, setCategoryId] = useState<number>(0);
    const [categories, setCategories] = useState<Category[]>([]);
    const [activeCycle, setActiveCycle] = useState<CycleEvent | null>(null);

    // Table State
    const [finalDecisionFilter, setFinalDecisionFilter] = useState<number>(0);
    const [tableData, setTableData] = useState<FinalDecisionSubmission[]>([]);
    const [isTableLoading, setIsTableLoading] = useState(false);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [cycleRes, catRes] = await Promise.all([
                    eventCycleService.getActiveCycleEvent(),
                    submissionService.getAllCategories(true)
                ]);
                if (cycleRes.isSuccess && cycleRes.data) setActiveCycle(cycleRes.data);
                if (catRes.isSuccess && catRes.data) setCategories(catRes.data);
            } catch (error) {
                }
        };
        fetchDropdownData();
    }, []);

    const fetchTableData = async () => {
        if (!activeCycle) return;
        setIsTableLoading(true);
        try {
            const payload: GetFinalDecisionPayload = {
                categoryId: categoryId,
                cycleId: activeCycle.cycle.id,
                applicationType: 0, // Force 0
                finalDecision: finalDecisionFilter
            };
            const response = await approvalService.getFinalDecision(payload);
            if (response.isSuccess && response.data) {
                setTableData(response.data);
            } else {
                setTableData([]);
            }
        } catch (error) {
            setTableData([]);
        } finally {
            setIsTableLoading(false);
        }
    };

    // Auto-fetch when activeCycle is loaded
    useEffect(() => {
        if (activeCycle) {
            fetchTableData();
        }
    }, [activeCycle]);

    const tableColumns: TableColumn<FinalDecisionSubmission>[] = [
        { key: 'sn', label: 'S/N', render: (_, __, index) => (index ?? 0) + 1 },
        { key: 'authorName', label: 'Author Name', render: (_, row) => row.submission?.author?.person?.fullName || 'N/A' },
        { key: 'submissionNumber', label: 'Sub. No', render: (_, row) => row.submission?.submissionNumber || 'N/A' },
        { key: 'title', label: 'Title', render: (_, row) => row.submission?.title || 'N/A' },
        { key: 'type', label: 'Type', render: (_, row) => row.submission?.applicationType === 1 ? 'Books' : 'Journal' },
        { key: 'thematicArea', label: 'Thematic Area', render: (_, row) => row.submission?.category?.name || 'N/A' },
        { key: 'averageScore', label: 'Avg Score', render: (_, row) => row.finalScore ?? 'N/A' },
        { key: 'geoZone', label: 'Geo-Political Zone', render: (_, row) => row.submission?.author?.person?.state?.geoPoliticalZone?.name || 'N/A' },
        { key: 'decision', label: 'Status', render: (_, row) => row.finalDecision === 0 ? <span className="text-green-600 font-bold">Approved</span> : <span className="text-red-600 font-bold">Rejected</span> }
    ];

    // Build the export parameters object matching the filter payload
    const exportParameters = {
        categoryId,
        cycleId: activeCycle?.cycle?.id ?? 0,
        applicationType: 0, // Force 0
        finalDecision: finalDecisionFilter,
    };

    return (
        <div className="w-full">
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Final Assessment Lists</h1>
                    <p className="text-gray-500">Export approved or rejected submissions for {activeCycle?.cycle?.name || 'Current Cycle'}.</p>
                </div>
                <button
                    onClick={() => navigate('/admin/collective-assessment')}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 bg-white border border-gray-300 px-4 py-2 rounded-lg transition-colors shadow-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Collective Assessment
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Filters and Table */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Filter Submissions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            {/* <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Submission Type</label>
                                <select
                                    value={applicationType}
                                    onChange={(e) => setApplicationType(Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 outline-none bg-gray-50"
                                >
                                    <option value={1}>Books</option>
                                    <option value={2}>Journal</option>
                                </select>
                            </div> */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Thematic Area</label>
                                <select
                                    value={categoryId}
                                    onChange={(e) => setCategoryId(Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 outline-none bg-gray-50"
                                >
                                    <option value={0}>Select Thematic Area</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Decision</label>
                                <select
                                    value={finalDecisionFilter}
                                    onChange={(e) => setFinalDecisionFilter(Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 outline-none bg-gray-50"
                                >
                                    <option value={0}>Approved</option>
                                    <option value={1}>Rejected</option>
                                </select>
                            </div>
                            <button
                                onClick={fetchTableData}
                                disabled={isTableLoading || !activeCycle}
                                className="w-full px-4 py-2 bg-[#15803D] text-white rounded-lg font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
                            >
                                {isTableLoading ? 'Fetching...' : 'Fetch Data'}
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <ReusableTable
                            columns={tableColumns}
                            data={tableData}
                            loading={isTableLoading}
                            title={`${finalDecisionFilter === 0 ? 'Approved' : 'Rejected'} Results`}
                            emptyMessage={`No ${finalDecisionFilter === 0 ? 'approved' : 'rejected'} submissions found.`}
                            showSearch={false}
                        />
                    </div>
                </div>

                {/* Right Column: Export Tools */}
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center">
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 ring-8 ring-green-50/50">
                            <FileSpreadsheet className="w-10 h-10 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 text-center">Export Results</h3>
                        <p className="text-gray-500 text-center mt-2 mb-8 max-w-[200px]">
                            Download or email the {finalDecisionFilter === 0 ? 'approved' : 'rejected'} submissions list.
                        </p>

                        {/* <ExportButton
                            reportType={4}
                            parameters={exportParameters}
                            reportTitle={`${finalDecisionFilter === 0 ? 'Approved' : 'Rejected'} Submissions List`}
                            buttonText={`Export ${finalDecisionFilter === 0 ? 'Approved' : 'Rejected'} List`}
                            className="w-full"
                        /> */}

                        {tableData.length === 0 && (
                            <p className="text-[10px] text-red-500 font-bold uppercase mt-4 tracking-widest animate-pulse">
                                No data available to export
                            </p>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
}

export default FinalAssessment;
